import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { admin as adminApi } from "@/lib/api";
import { Loader2, Save, Send, CheckCircle2, MessageCircle } from "lucide-react";

export default function Admin() {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && (!user || !user.is_admin)) nav("/");
  }, [user, ready, nav]);

  useEffect(() => {
    if (!user?.is_admin) return;
    adminApi.stats().then(setStats).catch(() => {});
    adminApi.settings().then(setSettings).catch(() => {});
    adminApi.orders().then(setOrders).catch(() => {});
    adminApi.users().then(setUsers).catch(() => {});
    adminApi.projects().then(setProjects).catch(() => {});
  }, [user]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const s = await adminApi.updateSettings(settings);
      setSettings(s);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!user?.is_admin) return null;

  const tabs = ["stats", "pricing", "orders", "users", "projects"];

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="pt-10 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="kd-tag mb-3">Admin</div>
          <h1 className="kd-display text-4xl md:text-5xl tracking-tight mb-8">Control room.</h1>

          <div className="flex gap-2 mb-8 flex-wrap">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`admin-tab-${t}`}
                className={`rounded-full px-5 py-2 text-xs tracking-[0.2em] uppercase transition-all ${
                  tab === t ? "bg-[#E50914] text-white kd-red-glow" : "bg-white/[0.03] border border-white/[0.08] text-zinc-300 hover:bg-white/[0.06]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === "stats" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Projects" value={stats.total_projects} />
              <StatCard label="Users" value={stats.total_users} />
              <StatCard label="Decks Ready" value={stats.total_ready} />
              <StatCard label="Failed" value={stats.total_failed} />
              <StatCard label="Paid Orders" value={stats.paid_orders} />
              <StatCard label="Revenue" value={`$${stats.revenue}`} />
            </div>
          )}

          {tab === "pricing" && settings && (
            <div className="kd-panel p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["price_1_page", "1 Page ($)"],
                  ["price_5_pages", "5 Pages ($)"],
                  ["price_10_pages", "10 Pages ($)"],
                  ["price_additional_page", "Additional Page ($)"],
                  ["price_revision", "Revision ($)"],
                  ["price_premium_graphics", "Premium Graphics ($)"],
                ].map(([k, label]) => (
                  <label key={k} className="block">
                    <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">{label}</div>
                    <input
                      type="number" step="0.01"
                      value={settings[k]}
                      onChange={(e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) })}
                      data-testid={`admin-${k}`}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30"
                    />
                  </label>
                ))}
                <label className="flex items-center gap-3 mt-2">
                  <input type="checkbox" checked={settings.pre_payment_enabled} onChange={(e) => setSettings({ ...settings, pre_payment_enabled: e.target.checked })} data-testid="admin-pre-payment" className="accent-[#E50914]" />
                  <span className="text-white/90 text-sm">Require payment before generation</span>
                </label>
                <label className="flex items-center gap-3 mt-2">
                  <input type="checkbox" checked={settings.post_payment_enabled} onChange={(e) => setSettings({ ...settings, post_payment_enabled: e.target.checked })} data-testid="admin-post-payment" className="accent-[#E50914]" />
                  <span className="text-white/90 text-sm">Allow post-generation payment</span>
                </label>
              </div>
              <button onClick={saveSettings} disabled={saving} data-testid="admin-save" className="mt-6 rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
              </button>
            </div>
          )}

          {tab === "orders" && (
            <TableView headers={["When", "Lookup", "Amount", "Status"]} rows={orders.map((o) => [
              new Date(o.created_at).toLocaleString(), o.lookup_key, `$${o.amount}`, o.payment_status,
            ])} />
          )}
          {tab === "users" && (
            <TableView headers={["When", "Name", "Email", "Phone", "Admin"]} rows={users.map((u) => [
              new Date(u.created_at).toLocaleDateString(), u.name || "—", u.email, u.phone, u.is_admin ? "yes" : "no",
            ])} />
          )}
          {tab === "projects" && (
            <div className="kd-panel p-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase">
                    {["When", "Stage Name", "Creator", "Pages", "Status", "Paid", "Owner", "Actions"].map((h) =>
                      <th key={h} className="text-left py-3 px-3 font-medium">{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr><td className="py-6 text-zinc-500" colSpan={8}>No projects yet.</td></tr>
                  ) : projects.map((p) => (
                    <tr key={p.id} className="border-t border-white/[0.05] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-3 text-zinc-300">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-3 text-white">{p.info?.stage_name || "Untitled"}</td>
                      <td className="py-3 px-3 text-zinc-300">{p.creator_type}</td>
                      <td className="py-3 px-3 text-zinc-300">{p.deck_size}</td>
                      <td className="py-3 px-3">
                        <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full ${
                          p.status === "ready" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                          : p.status === "generating" ? "bg-white/10 text-white border border-white/15"
                          : p.status === "failed" ? "bg-red-500/15 text-red-300 border border-red-500/30"
                          : "bg-white/5 text-zinc-400 border border-white/10"
                        }`}>{p.status}</span>
                      </td>
                      <td className="py-3 px-3 text-zinc-300">{p.paid ? "yes" : "no"}</td>
                      <td className="py-3 px-3 text-zinc-500 text-[11px] font-mono">{p.user_id ? p.user_id.slice(0, 6) : "—"}</td>
                      <td className="py-3 px-3">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => nav(`/create/${p.id}`)}
                            data-testid={`admin-project-open-${p.id}`}
                            className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
                          >
                            Open
                          </button>
                          {!p.user_id && (
                            <button
                              onClick={async () => {
                                try {
                                  await adminApi.assign(p.id);
                                  toast.success("Assigned to you");
                                  const list = await adminApi.projects();
                                  setProjects(list);
                                } catch { toast.error("Assign failed"); }
                              }}
                              data-testid={`admin-project-assign-${p.id}`}
                              className="rounded-full px-3 py-1 text-[11px] bg-[#E50914]/15 hover:bg-[#E50914]/25 border border-[#E50914]/40 text-[#FF2A2D] transition-colors"
                            >
                              Assign to me
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "support" && (
            <AdminSupport />
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-6 bg-white/[0.02] border border-white/[0.06]">
      <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-2">{label}</div>
      <div className="kd-display text-4xl">{value}</div>
    </motion.div>
  );
}

function AdminSupport() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    adminApi.supportThreads().then(setThreads).catch(() => {});
    const t = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (tick > 0) adminApi.supportThreads().then(setThreads).catch(() => {});
    if (selected) adminApi.supportThread(selected).then(setDetail).catch(() => {});
    // eslint-disable-next-line
  }, [tick]);

  const open = async (id) => {
    setSelected(id);
    try {
      const d = await adminApi.supportThread(id);
      setDetail(d);
    } catch { toast.error("Could not open thread"); }
  };

  const reply = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || !selected) return;
    setBusy(true);
    setText("");
    try {
      await adminApi.supportReply(selected, t);
      const d = await adminApi.supportThread(selected);
      setDetail(d);
    } catch { toast.error("Reply failed"); } finally { setBusy(false); }
  };

  const resolve = async () => {
    if (!selected) return;
    try {
      await adminApi.supportResolve(selected);
      toast.success("Marked resolved");
      const list = await adminApi.supportThreads();
      setThreads(list);
      const d = await adminApi.supportThread(selected);
      setDetail(d);
    } catch { toast.error("Could not resolve"); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">
      {/* Thread list */}
      <div className="kd-panel p-3 max-h-[70vh] overflow-y-auto">
        {threads.length === 0 ? (
          <div className="text-zinc-500 text-xs p-4">No conversations yet.</div>
        ) : threads.map((t) => (
          <button
            key={t.id}
            onClick={() => open(t.id)}
            data-testid={`admin-thread-${t.id}`}
            className={`w-full text-left p-3 rounded-2xl mb-1 transition-colors ${
              selected === t.id ? "bg-white/[0.08] border border-white/[0.12]" : "hover:bg-white/[0.04] border border-transparent"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="text-white text-sm truncate">{t.name || t.email}</div>
              {t.unread_admin > 0 && (
                <span className="bg-[#E50914] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{t.unread_admin}</span>
              )}
            </div>
            <div className="text-zinc-500 text-[11px] truncate mt-0.5">
              {t.last_message ? (
                <>
                  <span className="text-zinc-600 uppercase mr-1">[{t.last_message.sender}]</span>
                  {t.last_message.text}
                </>
              ) : "—"}
            </div>
            <div className="text-zinc-600 text-[10px] mt-1">
              {t.resolved ? <span className="text-[#FF2A2D]/70">Resolved · </span> : null}
              {new Date(t.last_message_at).toLocaleString()}
            </div>
          </button>
        ))}
      </div>

      {/* Conversation */}
      <div className="kd-panel p-4 flex flex-col min-h-[60vh]">
        {!detail ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <div className="text-center">
              <MessageCircle size={22} className="mx-auto mb-3 text-zinc-600" />
              Pick a conversation on the left to reply.
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/[0.06]">
              <div>
                <div className="text-white font-medium">{detail.thread.name || detail.thread.email}</div>
                <div className="text-zinc-500 text-[11px]">{detail.thread.email}</div>
              </div>
              <button
                onClick={resolve}
                data-testid="admin-thread-resolve"
                className="rounded-full px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-1.5"
              >
                <CheckCircle2 size={12} /> {detail.thread.resolved ? "Resolved" : "Mark resolved"}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2.5 mb-3 pr-2">
              {detail.messages.map((m) => {
                const isAdmin = m.sender === "admin";
                return (
                  <div key={m.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                      isAdmin ? "bg-[#E50914] text-white rounded-br-sm" : "bg-white/[0.05] border border-white/[0.08] text-white rounded-bl-sm"
                    }`}>
                      <div className="whitespace-pre-wrap break-words leading-relaxed">{m.text}</div>
                      <div className={`text-[10px] mt-1 ${isAdmin ? "text-white/70" : "text-zinc-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={reply} className="flex items-end gap-2">
              <textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(e); } }}
                placeholder="Reply as KlickDeck team…"
                data-testid="admin-reply-input"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30 resize-none max-h-32"
              />
              <button
                type="submit" disabled={busy || !text.trim()}
                data-testid="admin-reply-send"
                className="rounded-full p-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white disabled:opacity-40 transition-all"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function TableView({ headers, rows }) {
  return (
    <div className="kd-panel p-6 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase">
            {headers.map((h) => <th key={h} className="text-left py-3 px-3 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td className="py-6 text-zinc-500" colSpan={headers.length}>Nothing yet.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-white/[0.05] hover:bg-white/[0.02] transition-colors">
              {r.map((c, j) => <td key={j} className="py-3 px-3 text-zinc-200">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
